import { useState } from 'react';
import MarkdownEditor from '../shared/MarkdownEditor';

export default function ItemDetailsModal({ item, isOpen, onClose, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen || !item) return null;

  const handleEdit = () => {
    setEditedContent(item.content || item.description || '');
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!onUpdate) {
      console.error('No onUpdate handler provided');
      return;
    }

    setIsSaving(true);
    try {
      await onUpdate(item.id, { content: editedContent });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save item:', error);
      // Error is already handled by parent
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isEditing) {
      setIsEditing(false);
      setEditedContent('');
    } else {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
          <div>
            {item.title === "Generating title..." ? (
              <div className="flex items-center gap-2">
                <div className="h-6 w-48 bg-gray-700 rounded animate-pulse"></div>
                <svg className="animate-spin h-4 w-4 text-purple-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : (
              <h3 className="text-lg font-semibold text-white">{item.title}</h3>
            )}
            <p className="text-sm text-gray-400 mt-1">
              {item.title === "Generating title..." ? "AI generating metadata..." : "Item Details"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors duration-150"
          >
            <svg className="w-5 h-5 text-gray-400 hover:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 flex-1 overflow-y-auto">
          <MarkdownEditor
            content={item.content || item.description || ''}
            onSave={handleSave}
            onChange={setEditedContent}
            externalControls={true}
            isEditing={isEditing}
            onEdit={handleEdit}
            fontSize="text-sm"
            showBorder={true}
            textareaClassName="h-full"
            className="h-full"
          />
        </div>

        {/* Footer Buttons */}
        <div className="px-6 py-4 border-t border-gray-700 flex gap-2 justify-end flex-shrink-0">
          {!isEditing ? (
            <>
              <button
                onClick={handleEdit}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors duration-150 flex items-center gap-2"
                style={{ color: 'var(--color-text-on-accent)' }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </button>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors duration-150"
              >
                Close
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors duration-150"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 disabled:cursor-not-allowed rounded-lg transition-colors duration-150 flex items-center gap-2"
                style={{ color: 'var(--color-text-on-accent)' }}
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
